import { useDispatch, useSelector } from 'react-redux';

import { useConfig } from '@gaming1/g1-config';
import {
  checkIsCachedGeolocationValidRequestStateSelector,
  connectionToGeoComplyRequestStateSelector,
  coreActions,
  currentGeolocationSelector,
  decryptGeolocationRequestStateSelector,
  geoComplyLicenseKeyRequestStateSelector,
  geoComplyLicenseKeySelector,
  GeoComplyStatusCode,
  GeolocationReason,
  geolocationRequestStateSelector,
  geolocationTroubleshootersSelector,
  userLoggedInSelector,
} from '@gaming1/g1-core';
import { Box, Col, Heading, Row } from '@gaming1/g1-ui';
import { RemoteData } from '@gaming1/g1-utils';

import { useGeolocation } from '../../../geolocation/hooks/useGeolocation';

import { JsonDisplay, RequestStep, TriggerButton } from './styles';

const EXPIRED_GEOCOMPLY_LICENSE =
  '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';

export const GeolocationDebugger = () => {
  const dispatch = useDispatch();
  const {
    retryLatestGeolocationRequest,
    requestGeolocation,
    isGeoComplyReady,
  } = useGeolocation();
  const {
    core: {
      geoComply: { activated: isGeoComplyActivated },
    },
  } = useConfig();

  const isUserLoggedIn = useSelector(userLoggedInSelector);

  const currentGeolocation = useSelector(currentGeolocationSelector);
  const geoComplyLicenseKeyRequestState = useSelector(
    geoComplyLicenseKeyRequestStateSelector,
  );
  const geoComplyLicenseKey = useSelector(geoComplyLicenseKeySelector);
  const geolocationRequestState = useSelector(geolocationRequestStateSelector);
  const geolocationTroubleshooters = useSelector(
    geolocationTroubleshootersSelector,
  );

  const geocomplyConnectionRequestState = useSelector(
    connectionToGeoComplyRequestStateSelector,
  );
  const geolocationDecryptionRequestState = useSelector(
    decryptGeolocationRequestStateSelector,
  );
  const geolocationCacheCheckRequestState = useSelector(
    checkIsCachedGeolocationValidRequestStateSelector,
  );

  return (
    <>
      <Heading level="h1" mt="sm">
        Geolocation debugging page
      </Heading>
      <Heading level="h2">Requests state summary</Heading>
      <Box flexDirection="row" mt="sm">
        <RequestStep state={geoComplyLicenseKeyRequestState.status}>
          License
        </RequestStep>
        <RequestStep state={geocomplyConnectionRequestState.status}>
          Connection
        </RequestStep>
        <RequestStep state={geolocationCacheCheckRequestState.status}>
          Cache check
        </RequestStep>
        <RequestStep state={geolocationRequestState.status}>
          Request
        </RequestStep>
        <RequestStep state={geolocationDecryptionRequestState.status}>
          Decryption
        </RequestStep>
      </Box>
      <Heading level="h2">Geolocation</Heading>
      <p>
        The geolocation will be automatically asked when all those creterias are
        met:
      </p>
      <ul>
        <li>User is authenticated : {isUserLoggedIn ? 'Yes' : 'No'}</li>
        <li>GeoComply is activated : {isGeoComplyActivated ? 'Yes' : 'No'}</li>
        <li>
          The app is connected to GeoComply : {isGeoComplyReady ? 'Yes' : 'No'}
        </li>
      </ul>
      <Heading level="h3">Current geolocation</Heading>
      <Row>
        <Col xs={12} sm={8}>
          <JsonDisplay>
            {JSON.stringify(currentGeolocation, null, 2)}
          </JsonDisplay>
        </Col>
        <Col xs={12} sm={4}>
          <TriggerButton
            type="primary"
            loading={geolocationRequestState.status === RemoteData.Loading}
            onClick={() =>
              requestGeolocation(GeolocationReason.SessionIntervalGeo)
            }
          >
            Request a refresh of the geolocation
          </TriggerButton>
          {geolocationRequestState.status !== RemoteData.NotAsked && (
            <TriggerButton
              type="secondary"
              loading={geolocationRequestState.status === RemoteData.Loading}
              onClick={() => retryLatestGeolocationRequest()}
            >
              Retry the latest geolocation request
            </TriggerButton>
          )}
          <TriggerButton
            type="warning"
            onClick={() =>
              dispatch(
                coreActions.decryptGeolocation.success({
                  IsValid: false,
                  ServerTime: '2021-09-02T09:27:11.0374525Z',
                  ShouldGeoLocateBeforeTime: '2021-09-02T09:27:11.0374525Z',
                  ShouldGeoLocateInSeconds: 0,
                  Troubleshooters: [
                    {
                      Message:
                        "More location data is needed to confirm you're in a permitted area. Make sure location services are enabled or your device is in range of multiple wireless connections. Please address the items above, then try again.",
                      Rule: 'boundary',
                      ShowTryAgain: true,
                      HelpLink: 'https://youtu.be/l0iOQmx1qpo',
                    },
                    {
                      Message:
                        'Your device did not return any location information. Please make sure WiFi is turned on and Location Services are enabled, so we can accurately locate you and try again.',
                      Rule: 'location_services_flag',
                      ShowTryAgain: true,
                    },
                  ],
                }),
              )
            }
          >
            Trigger a mock geolocation result with troubleshooters
          </TriggerButton>
          <TriggerButton
            type="danger"
            onClick={() =>
              dispatch(
                coreActions.requestGeolocation.failure({
                  errorMessage:
                    'Mock geolocation request error created for debug purpose',
                  status: 9999,
                }),
              )
            }
          >
            Trigger an error with the geolocation request
          </TriggerButton>
        </Col>
      </Row>
      <Heading level="h3">Geolocation request state</Heading>
      <JsonDisplay>
        {JSON.stringify(geolocationRequestState, null, 2)}
      </JsonDisplay>
      <Heading level="h3">Geolocation troubleshooters</Heading>
      <JsonDisplay>
        {JSON.stringify(geolocationTroubleshooters, null, 2)}
      </JsonDisplay>
      <Heading level="h2">Geolocation license</Heading>
      <p>
        The license is fetched from the backend (it changes every day) and
        necessary to ask a geolocation
      </p>
      <Heading level="h3">License</Heading>
      <JsonDisplay
        style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
      >
        {(geoComplyLicenseKey || '').substr(0, 150)}
        {geoComplyLicenseKey ? '...' : ''}
      </JsonDisplay>
      <Heading level="h3">Geolocation license request state</Heading>
      <Row>
        <Col xs={12} sm={8}>
          <JsonDisplay>
            {JSON.stringify(geoComplyLicenseKeyRequestState, null, 2)}
          </JsonDisplay>
        </Col>
        <Col xs={12} sm={4}>
          <TriggerButton
            type="secondary"
            loading={
              geoComplyLicenseKeyRequestState.status === RemoteData.Loading
            }
            onClick={() =>
              dispatch(coreActions.getGeoComplyLicenseKey.request())
            }
          >
            Ask for the license again
          </TriggerButton>

          <TriggerButton
            type="warning"
            onClick={() =>
              dispatch(
                coreActions.getGeoComplyLicenseKey.success({
                  License: EXPIRED_GEOCOMPLY_LICENSE,
                }),
              )
            }
          >
            Inject an expired license key
          </TriggerButton>

          <TriggerButton
            type="danger"
            onClick={() =>
              dispatch(
                coreActions.getGeoComplyLicenseKey.failure({
                  errorMessage: 'Mock license error created for debug purpose',
                  status: 9999,
                }),
              )
            }
          >
            Trigger a mock license error
          </TriggerButton>
        </Col>
      </Row>
      <Heading level="h2">Other requests</Heading>
      <Heading level="h3">Geocomply connection</Heading>
      <p>
        The app should be able to connect to the Geocomply PLC (desktop) or
        module of the native app (iOS/Android)
      </p>
      )
      <Row>
        <Col xs={12} sm={8}>
          <JsonDisplay>
            {JSON.stringify(geocomplyConnectionRequestState, null, 2)}
          </JsonDisplay>
        </Col>
        <Col xs={12} sm={4}>
          <TriggerButton
            type="secondary"
            loading={
              geocomplyConnectionRequestState.status === RemoteData.Loading
            }
            onClick={() => dispatch(coreActions.connectToGeoComply.request())}
          >
            Retry to connect to the Geocomply Plugin
          </TriggerButton>
          <TriggerButton
            type="danger"
            onClick={() =>
              dispatch(
                coreActions.connectToGeoComply.failure({
                  errorMessage: 'Mock: The PLC is not installed',
                  status:
                    GeoComplyStatusCode.CLNT_ERROR_LOCAL_SERVICE_UNAVAILABLE,
                }),
              )
            }
          >
            Trigger a mock connection error (PLC not installed)
          </TriggerButton>
          <TriggerButton
            type="danger"
            onClick={() =>
              dispatch(
                coreActions.connectToGeoComply.failure({
                  errorMessage: 'Mock: The PLC could not be contacted',
                  status:
                    GeoComplyStatusCode.CLNT_ERROR_LOCAL_SERVICE_COMMUNICATION,
                }),
              )
            }
          >
            Trigger a mock connection error to the PLC
          </TriggerButton>
        </Col>
      </Row>
      <Heading level="h3">Geocomply cache check</Heading>
      <p>
        A check made to ensure the cached request linked to the user is still
        valid (to avoid request geolocations when not needed). If not valid,
        trigger another geolocation request.
      </p>
      <Row>
        <Col xs={12} sm={8}>
          <JsonDisplay>
            {JSON.stringify(geolocationCacheCheckRequestState, null, 2)}
          </JsonDisplay>
        </Col>
        <Col xs={12} sm={4}>
          <TriggerButton
            type="secondary"
            loading={
              geolocationCacheCheckRequestState.status === RemoteData.Loading
            }
            onClick={() =>
              dispatch(coreActions.checkIsCachedGeolocationValid.request())
            }
          >
            Check the cached data
          </TriggerButton>
        </Col>
      </Row>
      <Heading level="h3">Geocomply result decryption</Heading>
      <p>The result of the decrypted geolocation result by the backend</p>
      <Row>
        <Col xs={12} sm={8}>
          <JsonDisplay>
            {JSON.stringify(geolocationDecryptionRequestState, null, 2)}
          </JsonDisplay>
        </Col>
        <Col xs={12} sm={4}>
          <TriggerButton
            type="danger"
            onClick={() =>
              dispatch(
                coreActions.decryptGeolocation.failure({
                  errorMessage:
                    'Mock decryption error created for debug purpose',
                  status: 9999,
                }),
              )
            }
          >
            Trigger a mock geolocation decryption error
          </TriggerButton>
        </Col>
      </Row>
    </>
  );
};
